import {Color} from "@/lib/styles";
import ActionButton from "@/lib/components/ActionButton";
import Page from "@/lib/pages/Page";
import {useState} from "react";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import Message from "@/lib/components/Message";
import {useSession} from "@/lib/auth/auth";
import {BackendError, PasswordResetRequest} from "@/lib/types";
import {fetchBackendNoContent} from "@/lib/backend";
import {TextInput} from "react-native";
import globstyles from "@/lib/globstyles";
import {useLocalSearchParams, useRouter} from "expo-router";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Center from "@/lib/components/Center";

export default function ResetPasswordPage() {
    const {id, token} = useLocalSearchParams();
    const {session} = useSession();
    const router = useRouter();

    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [emailSent, setEmailSent] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const [expired, setExpired] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);

    async function requestEmail() {
        setBusy(true);
        setSaveError(null);
        const changes: PasswordResetRequest = {
            username,
        };
        fetchBackendNoContent(`/account/password`, "POST", session, changes).then(async () => {
            setEmailSent(true);
        }).catch((error) => {
            setSaveError(error);
        }).finally(() => {
            setBusy(false);
        });
    }

    async function requestReset() {
        setBusy(true);
        setSaveError(null);
        const changes: PasswordResetRequest = {
            password,
            password_reset_id: id ? id.toString() : undefined,
            password_reset_token: token ? token.toString() : undefined,
        };
        fetchBackendNoContent(`/account/password`, "POST", session, changes).then(async () => {
            setResetSuccess(true);
        }).catch((error) => {
            setSaveError(error);
            const backendError = error as BackendError;
            if (backendError && backendError.status === 403) {
                setExpired(true);
            }
        }).finally(() => {
            setBusy(false);
        });
    }

    function resetProcess() {
        setSaveError(null);
        router.navigate(`/u/account/resetpassword`)
    }

    if (emailSent) {
        return (
            <Page>
                <Center width={330}>
                    <Header title={"Sjekk e-post"} center={true}/>
                    <Message
                        text={"Finn fram e-posten vi sendte deg. Den inneholder en lenke du må trykke på for å nullstille passordet."}/>
                </Center>
            </Page>
        )
    }

    if (resetSuccess) {
        return (
            <Page>
                <Center width={330}>
                    <Header title={"Passordet ditt er endret"}/>
                    <Message text={"Du kan nå logge inn med ditt nye passord."}/>
                    <Nav>
                        <ActionButton title={"Gå til innlogging"} onPress={() => router.replace(`/u`)}/>
                    </Nav>
                </Center>
            </Page>
        )
    }

    // ID and password token present; prompt for password
    if (!!id && !!token) {
        return (
            <Page>
                <Center width={330}>
                    <Header title={"Lag nytt passord"}/>
                    <Message text={"Skriv inn et nytt passord i feltet under."}/>
                    <TextInput style={globstyles.formTextInput}
                               placeholder={"Passord"}
                               placeholderTextColor={Color.FadedText}
                               textContentType={"password"}
                               secureTextEntry={true}
                               value={password}
                               onChangeText={(password) => {
                                   setPassword(password);
                                   setSaveError(null);
                               }}
                               returnKeyType={"send"}
                               autoFocus={true}
                               focusable={true}
                               enablesReturnKeyAutomatically={true}
                               keyboardAppearance={"dark"}
                               autoComplete={"new-password"}
                               onSubmitEditing={requestReset}
                    />

                    {saveError && <ErrorMessage error={saveError}/>}

                    <Nav>
                        {!expired &&
                            <ActionButton title={"Lagre passord"} icon={"checkmark"} busy={busy} color={Color.Green}
                                          onPress={requestReset}/>}
                        {expired && <ActionButton title={"Start på nytt"} onPress={resetProcess}/>}
                        <ActionButton title={"Avbryt"} onPress={() => router.dismiss()}/>
                    </Nav>
                </Center>
            </Page>
        )
    }

    // Link not clicked, we are just starting out.
    return (
        <Page>
            <Center width={330}>
                <Header title={"Nullstill passord"}/>
                <Message
                    text={"Angi enten brukernavn eller e-postadresse, og vi sender deg en lenke på e-post som du må bruke for å bytte passord."}/>
                <TextInput style={globstyles.formTextInput}
                           placeholder={"Brukernavn eller e-postadresse"}
                           placeholderTextColor={Color.FadedText}
                           value={username}
                           onChangeText={(value) => {
                               setUsername(value);
                               setSaveError(null);
                           }}
                           inputMode={"text"}
                           returnKeyType={"send"}
                           enablesReturnKeyAutomatically={true}
                           autoFocus={true}
                           focusable={true}
                           keyboardAppearance={"dark"}
                           submitBehavior={"submit"}
                           onSubmitEditing={() => requestEmail()}
                />

                {saveError && <ErrorMessage error={saveError}/>}

                <Nav>
                    <ActionButton title={"Send lenken"} color={Color.Green} busy={busy} onPress={requestEmail}/>
                    <ActionButton title={"Avbryt"} onPress={() => router.navigate(`/u/account/login`)}/>
                </Nav>
            </Center>
        </Page>
    );
}